import { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import {
  alignItems,
  destinationOptions,
  fileTypes,
  routePaths,
  strCode,
  systemNotificationOptions
} from "../../../consts";
import { log, notificationService, t } from "../../../services";
import { fileUtils, notificationUtils, objectUtil } from "../../../utils";

import {
  DropdownInput,
  FileInput,
  FlexContainers,
  Input,
  Paragraph2,
  WrapperContainer
} from "../../../components";
import { FormSection } from "../../../templates";
import { LocalizationConfig } from "./components/LocalIzationConfig/LocalIzationConfig";
import { ButtonsWrapper, FormButton, InputSectionWrapper, NotificationFormWrapper } from "./styled";

const NewNotificationForm = ({
  notificationsData,
  isSystemNotification,
  availableSystemNotificationOptions = systemNotificationOptions,
  onSaveNotification
}) => {
  const [notificationName, setNotificationName] = useState(null);
  const [destination, setDestination] = useState("");

  const [image, setImage] = useState();
  const [localizations, setLocalizations] = useState([]);

  const [isValid, setIsValid] = useState(true);
  const [isSameData, setIsSameData] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const [loading, setLoading] = useState(false);

  const history = useHistory();

  const isImageUploaded = useMemo(() => {
    return !!image?.name;
  }, [image?.name]);

  const getNotificationData = useCallback(() => {
    let newData = {
      notificationName,
      destination: destination?.id,
      image: image,
      localizations: localizations.map((item) => {
        return {
          language: item?.title,
          title: item?.config?.title,
          bodyText: item?.config?.bodyText
        };
      })
    };

    return newData;
  }, [notificationName, destination, image, localizations]);

  const getOriginalNotificationData = useCallback(() => {
    let originalData = {
      notificationName: notificationsData?.notificationName,
      destination: notificationsData?.destination,
      image: notificationsData?.imageFile,
      localizations: notificationsData?.localizations ?? []
    };

    return originalData;
  }, [notificationsData]);

  useEffect(() => {
    prepopulateData();
  }, [notificationsData]);

  useEffect(() => {
    loadImage();
  }, [notificationsData?.imageFile]);

  useEffect(() => {
    let valid = !!notificationName && !!destination && !!localizations.length;
    let isSame = false;

    const localisationErrorFields = [];

    if (localizations.length) {
      localizations.map((item) => {
        const { title, bodyText } = item?.config ?? {};

        if (!title || !bodyText) {
          const errorConfig = { language: item.title, title: !title, bodyText: !bodyText };
          localisationErrorFields.push(errorConfig);
        }
      });
    }

    if (localisationErrorFields?.length) {
      valid = false;
    }

    const errorFields = {
      notificationName: !notificationName,
      destination: !destination,
      localizations: localisationErrorFields
    };

    setMissingFields(errorFields);

    if (notificationsData) {
      const originalData = getOriginalNotificationData();
      const newData = getNotificationData();

      delete originalData.image;
      delete newData.image;

      if (objectUtil.objEquals(originalData, newData)) {
        isSame = true;
      }
    }

    setIsValid(valid);
    setIsSameData(isSame);
  }, [notificationName, destination, image, localizations]);

  const prepopulateData = useCallback(() => {
    if (notificationsData) {
      const name = notificationsData?.notificationName;

      if (isSystemNotification) {
        setNotificationName(systemNotificationOptions.find((opt) => opt.id === name));
      } else {
        setNotificationName(name);
      }

      setDestination(destinationOptions.find((opt) => opt.id === notificationsData?.destination));

      if (notificationsData?.imageFile && !image) {
        setImage(notificationsData.imageFile);
      }

      const localizationsList = (notificationsData?.localizations ?? []).map((item, index) => {
        return {
          id: index,
          title: item?.language,
          config: {
            title: item?.title,
            bodyText: item?.bodyText
          }
        };
      });

      setLocalizations(localizationsList);
    }
  }, [notificationsData, isSystemNotification]);

  const loadImage = useCallback(() => {
    if (notificationsData?.imageFile && !image) {
      setImage(notificationsData.imageFile);
    }
  }, [notificationsData?.imageFile, image]);

  const resetFormData = () => {
    setNotificationName("");
    setDestination("");
    setImage(null);
    setLocalizations([]);
  };

  const onClose = () => {
    resetFormData();
    history.push(routePaths.NOTIFICATIONS);
  };

  const onSubmit = async () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (isSameData && image && !isImageUploaded) {
      onClose();
      return;
    }

    if (!isValid) {
      log.warn("NewNotificationForm: Unable to save notification due to missing fields");

      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
      return;
    }

    setLoading(true);
    const notification = getNotificationData();
    try {
      let binaryImage = null;
      if (isImageUploaded) {
        binaryImage = await fileUtils.readFile(image);
      }

      await onSaveNotification(
        notificationUtils.prepareNotificationData(notification, isSystemNotification),
        binaryImage,
        {
          isSameData,
          isSystemNotification
        }
      );
      resetFormData();
    } catch (e) {
      log.error(`An error occurred while saving notification data.`, e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      width={"100%"}
      height={"100%"}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnBetween}
        width={"100%"}
        height={"100%"}
        pt={36}>
        {loading ? (
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            width={"100%"}
            mt={200}
            textAlign={"center"}>
            <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
          </WrapperContainer>
        ) : (
          <>
            <WrapperContainer width={"100%"} height={"100%"}>
              <NotificationFormWrapper
                Container={FlexContainers.FlexRowStart}
                alignItems={alignItems.start}
                width={"100%"}
                gap={16}>
                <FormSection title={t(strCode.OVERVIEW)}>
                  <InputSectionWrapper Container={FlexContainers.FlexColumnStart} mb={18}>
                    {isSystemNotification ? (
                      <DropdownInput
                        id="notification-name-selector"
                        title={t(strCode.NAME_TITLE)}
                        placeholder={t(strCode.NAME_TITLE)}
                        options={availableSystemNotificationOptions}
                        selectedOption={notificationName}
                        onSelectOption={(id) => {
                          setNotificationName(
                            systemNotificationOptions.find((opt) => opt.id === id)
                          );
                        }}
                        hasError={showErrors && missingFields?.notificationName}
                        disabled={!!notificationsData}
                      />
                    ) : (
                      <Input
                        id={"notificationName"}
                        title={t(strCode.NAME_TITLE)}
                        value={notificationName}
                        setValue={setNotificationName}
                        placeholder={t(strCode.NAME_TITLE)}
                        hasError={showErrors && missingFields?.notificationName}
                        disabled={!!notificationsData}
                      />
                    )}

                    <DropdownInput
                      id="destination-selector"
                      title={t(strCode.DESTINATION_TITLE)}
                      placeholder={t(strCode.DESTINATION_PLACEHOLDER)}
                      options={destinationOptions}
                      selectedOption={destination}
                      onSelectOption={(id) => {
                        setDestination(destinationOptions.find((opt) => opt.id === id));
                      }}
                      hasError={showErrors && missingFields?.destination}
                    />

                    <FileInput
                      id="image"
                      title={t(strCode.IMAGE_TITLE)}
                      file={image}
                      setFile={setImage}
                      fileType={fileTypes.image}
                      defaultFileName={
                        notificationsData?.notificationName
                          ? `${notificationsData?.notificationName}.jpg`
                          : null
                      }
                      hasError={showErrors && missingFields?.image}
                    />
                  </InputSectionWrapper>
                </FormSection>

                <FormSection title={t(strCode.LOCALIZATION_TITLE)}>
                  <LocalizationConfig
                    languageList={localizations}
                    setLanguageList={setLocalizations}
                    missingFields={showErrors ? missingFields?.localizations : []}
                    viewOnlyLangs={isSystemNotification ? ["EN"] : null}
                  />
                </FormSection>
              </NotificationFormWrapper>
            </WrapperContainer>

            <ButtonsWrapper Container={FlexContainers.FlexRowEnd} width="100%" gap={8} mt={11}>
              <FormButton secondary onClick={onClose}>
                {t(strCode.CANCEL_BTN)}
              </FormButton>
              <FormButton onClick={onSubmit}>{t(strCode.SAVE_BTN)}</FormButton>
            </ButtonsWrapper>
          </>
        )}
      </WrapperContainer>
    </WrapperContainer>
  );
};

NewNotificationForm.propTypes = {
  notificationsData: PropTypes.object,
  isSystemNotification: PropTypes.bool.isRequired,
  availableSystemNotificationOptions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
  ).isRequired,
  onSaveNotification: PropTypes.func.isRequired
};

export default NewNotificationForm;
